import React, { useState, useEffect } from 'react';
import { MapPin, AlertCircle } from 'lucide-react';
import { updateRoomConfig, clearRoomConfig } from '@/config/roomConfig';
import departmentsService from '@/domains/internal/facilities/services/departmentService';
import roomsService from '@/domains/internal/facilities/services/roomService';
import deviceService from '@/domains/internal/facilities/services/deviceService';
import { ComboBox, Modal } from '@/shared/components/ui';
import { Button } from '@/shared/components/ui/Button';

interface RoomSetupModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  canClose?: boolean; // If false, user must set room before proceeding
}

interface Room {
  room_id: string;
  room_name: string;
  room_number: string;
  department_id?: string;
  department_name?: string;
}

const RoomSetupModal: React.FC<RoomSetupModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  canClose = true,
}) => {
  const [selectedRoomId, setSelectedRoomId] = useState('');
  const [rooms, setRooms] = useState<Room[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      loadData();
    }
  }, [isOpen]);

  const loadData = async () => {
    try {
      const [roomsResponse] = await Promise.all([
        roomsService.getRooms(),
        departmentsService.getDepartments()
      ]);
      setRooms(roomsResponse.data);
    } catch (err) {
      console.error('[RoomSetupModal] Failed to load data:', err);
      setError('Failed to load rooms and departments');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    setLoading(true);

    try {
      if (selectedRoomId === 'NO_ROOM') {
        // Clear room configuration both locally and on backend
        clearRoomConfig();
        try {
          await deviceService.updateRoomAssignment(null);
        } catch (backendError) {
          console.warn('[RoomSetupModal] Failed to clear room assignment on backend:', backendError);
          // Continue anyway - localStorage is cleared
        }
      } else {
        if (!selectedRoomId || selectedRoomId === '') {
          setError('Please select a room or choose "No Room Assigned"');
          setLoading(false);
          return;
        }

        // Find the selected room
        const selectedRoom = rooms.find(r => r.room_id === selectedRoomId);
        if (!selectedRoom) {
          setError('Selected room not found');
          setLoading(false);
          return;
        }

        // Register device with backend (primary)
        try {
          await deviceService.updateRoomAssignment(
            selectedRoom.room_id,
            `${selectedRoom.room_name} Terminal`
          );

          // Cache in localStorage (secondary)
          updateRoomConfig(
            selectedRoom.room_id,
            selectedRoom.room_name,
            selectedRoom.department_id
          );
        } catch (backendError) {
          console.error('Failed to register device with backend:', backendError);
          setError('Failed to register device with backend. Please check your connection and try again.');
          setLoading(false);
          return;
        }
      }

      // Success
      onSuccess();
      onClose();
    } catch (err) {
      setError('Failed to save room configuration');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (canClose) {
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={canClose ? onClose : () => {}}
      closeOnOverlayClick={canClose}
      showCloseButton={canClose}
      title={
        <div className="flex items-center gap-2">
          <MapPin className="w-6 h-6 text-blue-600" />
          <span>{canClose ? 'Change Room Assignment' : 'Room Setup Required'}</span>
        </div>
      }
      size="md"
      footer={
        <div className="flex gap-3 justify-end">
          {canClose && (
            <Button
              type="button"
              onClick={handleClose}
              variant="neutral"
              padding="px-4 py-2"
            >
              Cancel
            </Button>
          )}
          <Button
            type="submit"
            form="room-setup-form"
            disabled={loading}
            variant={selectedRoomId === 'NO_ROOM' ? 'neutral' : 'info'}
            padding="px-4 py-2"
          >
            {loading ? 'Saving...' : selectedRoomId === 'NO_ROOM' ? 'Skip and Continue' : 'Assign Room to This Device'}
          </Button>
        </div>
      }
    >
      <form id="room-setup-form" onSubmit={handleSubmit} className="space-y-4">
        {!canClose && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex gap-3">
            <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-amber-800">
              <p className="font-medium">Room assignment required</p>
              <p className="mt-1">
                Please configure the room for this device before proceeding.
              </p>
            </div>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
            {error}
          </div>
        )}

        {/* Room Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Select Room <span className="text-red-500">*</span>
          </label>
          <ComboBox
            value={selectedRoomId}
            onChange={setSelectedRoomId}
            options={[
              { value: 'NO_ROOM', label: 'No Room Assigned (Admin/Setup Only)' },
              ...rooms.map(room => ({
                value: room.room_id,
                label: `${room.room_name} (${room.room_number})${room.department_name ? ` - ${room.department_name}` : ''}`
              }))
            ]}
            placeholder="Select a room"
            searchable
            clearable
            fontSize="text-sm"
            padding="px-3 py-2"
          />
          <p className="mt-1 text-xs text-gray-500">
            {selectedRoomId === 'NO_ROOM'
              ? 'Use this when setting up rooms in the admin panel'
              : 'Choose the room where this device is located'}
          </p>
        </div>

        {/* Room Details (Read-only preview) */}
        {selectedRoomId && selectedRoomId !== 'NO_ROOM' && (() => {
          const selectedRoom = rooms.find(r => r.room_id === selectedRoomId);
          return selectedRoom ? (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm font-medium text-blue-900 mb-1">Room Details:</p>
              <div className="text-sm text-blue-800 space-y-1">
                <p><span className="font-medium">Name:</span> {selectedRoom.room_name}</p>
                <p><span className="font-medium">Number:</span> {selectedRoom.room_number}</p>
                {selectedRoom.department_name && (
                  <p><span className="font-medium">Department:</span> {selectedRoom.department_name}</p>
                )}
              </div>
            </div>
          ) : null;
        })()}
      </form>
    </Modal>
  );
};

export default RoomSetupModal;
